import { DOCUMENT_PREVIEW_LIMIT, loadPreviewDocument, PreviewDocumentLoader } from './files'

// One loader belongs to one review and user; documents never enter persistent storage.
export function createPreviewDocumentLoader(request: PreviewDocumentLoader = loadPreviewDocument): PreviewDocumentLoader {
  type Entry = { controller: AbortController; promise: Promise<ArrayBuffer>; buffer?: ArrayBuffer; readers: number }
  const entries = new Map<string, Entry>()
  const aborted = () => new DOMException('Загрузка отменена', 'AbortError')
  const trim = () => {
    let bytes = Array.from(entries.values()).reduce((sum, entry) => sum + (entry.buffer?.byteLength || 0), 0)
    for (const [key, entry] of entries) {
      if (bytes <= DOCUMENT_PREVIEW_LIMIT && entries.size <= 3) break
      if (!entry.buffer || entry.readers) continue
      bytes -= entry.buffer.byteLength
      entries.delete(key)
    }
  }
  return (file, signal) => {
    if (signal.aborted) return Promise.reject(aborted())
    const key = file.url
    let entry = entries.get(key)
    if (!entry) {
      const controller = new AbortController()
      const current: Entry = { controller, readers: 0, promise: Promise.resolve(new ArrayBuffer(0)) }
      current.promise = request(file, controller.signal).then(buffer => {
        current.buffer = buffer
        trim()
        return buffer
      }, error => {
        if (entries.get(key) === current) entries.delete(key)
        throw error
      })
      entries.set(key, current)
      entry = current
    } else {
      entries.delete(key)
      entries.set(key, entry)
    }
    const current = entry
    current.readers += 1
    return new Promise<ArrayBuffer>((resolve, reject) => {
      let settled = false
      const finish = (error?: unknown, buffer?: ArrayBuffer) => {
        if (settled) return
        settled = true
        signal.removeEventListener('abort', cancel)
        current.readers -= 1
        if (error) reject(error)
        else resolve(buffer!)
        trim()
        if (!current.readers && !current.buffer) {
          if (entries.get(key) === current) entries.delete(key)
          current.controller.abort()
        }
      }
      const cancel = () => finish(aborted())
      signal.addEventListener('abort', cancel, { once: true })
      current.promise.then(buffer => finish(undefined, buffer), error => finish(error))
    })
  }
}
